import React, { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Tooltip,
  Typography,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import { MovableCardWrapper } from '../common/board/styles/Base';
import MealTypesChips from './meal_types_chips';
import MealCardActions from './meal_card_actions';
import imgPlaceholder from '../../assets/images/imgPlaceholder.png';
import Nutrition from './nutrition';
import {
  allIngredientsStatus,
  mealFromSharedMeal,
  mealWithParentUpdated,
  mealWithScaledIngredientsReplaced,
  newMealWithDefaultFields,
} from '../../services/meals';
import { useIsMobile } from '../common/layout_hooks';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import {
  useCreateMealMutation,
  useGetProgrammesQuery,
  useGetSharedProgrammesQuery,
  useGetSmorgBoardQuery,
  useGetSpaceQuery,
  useLazyGetFullMealQuery,
  useLazyGetFullSharedMealQuery,
  useLazyGetProgrammesQuery,
  useLazyGetSmorgBoardQuery,
  useUpdateDbPlannerWeekOptimisticallyMutation,
  useUpdateMealMutation,
  useUpdateProgrammeMutation,
  useUpdateSmorgBoardMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { healthProGroupForSpaceID, userIsCreator } from '../../services/users';
import {
  programmePlanDayIDFromDayIndex,
  programmePlanWithEntryAdded,
  programmesWhichReferenceRecipesBoard,
  programmeWithPlanUpdated,
  sharedProgrammesWhichReferenceMeal,
} from '../../services/programmes';
import { ActivityObjectType, EntryType } from '../../API';
import {
  boardWithMealAdded,
  boardWithMealMoved,
  boardWithMealRemoved,
} from '../../services/smorg_board';
import { cloneObject } from '../../operations/utils';
import { trackEvents, useTracker } from '../../operations/tracking_operations';
import { copyMealToPlanner } from '../../operations/planner_operations';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetDistilledMealByIDAndParentQuery,
  useGetOwnedRecipesBoardsQuery,
  useGetDistilledSharedMealByIDAndParentQuery,
  useGetUserProfileQuery,
  useLazyGetOrCreateDbPlannerWeekByStartDateQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  copyMealToBoard,
  copySharedMealToBoard,
} from '../../operations/recipes_operations';
import {
  mealsAddedToMealBasket,
  mealsRemovedFromMealBasket,
} from '../../features/end_user_shopping/end_user_shopping_slice';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';
import { userFriendlyNameForActivitySignalSelector } from '../../features/user_id/user_id_slice';
// import StepInput from '../common/step_input';
import live_icon from '../../assets/images/live.png';
import { scaleMealIngredientsOperation } from '../../operations/meal_operations';

const MealCard = ({
  id,
  appBoardId: recipesBoardId,
  onClick,
  title,
  style,
  className,
  onDelete,
}) => {
  const dispatch = useDispatch();

  const isMobile = useIsMobile();

  const tracker = useTracker();
  const { data: userProfile } = useGetUserProfileQuery();

  const isSmorgStudio = userIsCreator(userProfile);

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery(
    undefined,
    {
      skip: !userProfile || isSmorgStudio,
    },
  );

  const { data: healthProSpace } = useGetSpaceQuery();

  const currentSpaceID = isSmorgStudio
    ? healthProSpace?.id
    : currentSpaceMembership?.spaceID;

  const { data: sharedProgrammes } = useGetSharedProgrammesQuery(
    healthProSpace?.id,
    {
      skip: !healthProSpace,
    },
  );

  const sharedProgrammesLinked = sharedProgrammesWhichReferenceMeal(
    id,
    sharedProgrammes || [],
  );

  const customerFoodDataID = customerFoodDataIDOfSpace(currentSpaceID);

  const { data: distilledMealInMeals } = useGetDistilledMealByIDAndParentQuery({
    id,
    parentID: recipesBoardId,
  });

  const { data: distilledMealInSharedMeals } =
    useGetDistilledSharedMealByIDAndParentQuery(
      { id, parentID: recipesBoardId },
      { skip: isSmorgStudio },
    );

  const [createMeal] = useCreateMealMutation();

  const [updateMeal] = useUpdateMealMutation();

  const [updateProgramme] = useUpdateProgrammeMutation();

  const distilledMeal = distilledMealInMeals || distilledMealInSharedMeals;

  // console.log(`MealCard recipesBoardId ${recipesBoardId}`);
  const isSharedMeal = !!distilledMealInSharedMeals;

  const distilledRecipe = (distilledMeal?.recipes || [])[0];

  const mealImageUrl = distilledRecipe?.imageUrl;

  const mealTypes = distilledRecipe?.mealTypes;

  const isReadOnly = style._readOnly;

  const menuID = style._menuID;

  const isInMealBasket = useSelector((state) =>
    state.endUserShopping.mealBasketItems.some((item) => item.meal.id === id),
  );
  const calories =
    distilledRecipe?.derivedNutrition?.totalNutritionPerServing?.calories;

  const boardEmbeddedInProgrammeID = false; /* useSelector(
    (state) =>
      recipesBoardByIdSelector(state, recipesBoardId)?.embeddedInContainerID,
  ) */
  const isBoardEmbeddedInProgramme = !!boardEmbeddedInProgrammeID;

  const allIngredientIDs = (distilledRecipe?.ingredients || []).map(
    (ing) => ing.id,
  );
  const ingredientNutrition =
    distilledMeal?.derivedNutrition?.ingredientNutrition;
  const allIngsStatus = allIngredientsStatus(
    allIngredientIDs,
    ingredientNutrition,
  );

  const currentHealthProGroup =
    healthProSpace && healthProGroupForSpaceID(healthProSpace.id);

  const { data: recipesBoard } = useGetSmorgBoardQuery(recipesBoardId, {
    skip: !recipesBoardId,
  });

  const [lazyGetOrCreateDbPlannerWeekByStartDate] =
    useLazyGetOrCreateDbPlannerWeekByStartDateQuery();

  const [updateDbPlannerWeek] = useUpdateDbPlannerWeekOptimisticallyMutation();

  const [updateSmorgBoard] = useUpdateSmorgBoardMutation();

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [lazyGetSharedMeal] = useLazyGetFullSharedMealQuery();

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  const onQuickDuplicate = useCallback(async () => {
    const sourceMenu = recipesBoard.menus.find((menu) =>
      menu.mealIDs.includes(id),
    );
    const currentPosition = sourceMenu.mealIDs.indexOf(id);
    const { data: fullMeal } = await lazyGetMeal(id);
    const mealWithoutId = cloneObject(fullMeal);
    const input = newMealWithDefaultFields(
      mealWithoutId,
      recipesBoard.id,
      currentSpaceMembership?.id,
      currentHealthProGroup,
    );

    const { data: copiedMeal } = await createMeal(input);
    const newBoard = boardWithMealAdded(
      recipesBoard,
      sourceMenu.id,
      copiedMeal,
      currentPosition + 1,
    );
    await updateSmorgBoard(newBoard);
    tracker(userProfile, [
      {
        name: 'Meal copied',
        args: {
          recipeTitle: copiedMeal.recipes[0]?.title,
          id: copiedMeal.id,
        },
      },
    ]);
    if (isSmorgStudio) {
      reportAccountActivitySignalOperation({
        spaceID: healthProSpace.id,
        friendlyUsername,
        lastActivityObjectType: ActivityObjectType.RECIPES,
        lastActivityObjectID: recipesBoardId,
      });
    }
  }, [
    recipesBoard,
    id,
    lazyGetMeal,
    currentSpaceMembership?.id,
    currentHealthProGroup,
    createMeal,
    updateSmorgBoard,
    tracker,
    userProfile,
    isSmorgStudio,
    healthProSpace?.id,
    friendlyUsername,
    recipesBoardId,
  ]);

  const onCopyToPlanner = useCallback(
    async (plannerViewWeekStartDate, dayIndexes, scaleToServings) => {
      let fullMealToCopy;
      if (isSharedMeal) {
        const { data: fullSharedMeal } = await lazyGetSharedMeal(id);
        fullMealToCopy = mealFromSharedMeal(fullSharedMeal);
      } else {
        const { data: fullMeal } = await lazyGetMeal(id);
        fullMealToCopy = fullMeal;
      }
      copyMealToPlanner(
        fullMealToCopy,
        plannerViewWeekStartDate,
        dayIndexes,
        customerFoodDataID,
        tracker,
        userProfile,
        currentSpaceMembership,
        lazyGetOrCreateDbPlannerWeekByStartDate,
        createMeal,
        updateMeal,
        updateDbPlannerWeek,
        lazyGetMeal,
        scaleToServings,
      );
    },
    [
      createMeal,
      currentSpaceMembership,
      customerFoodDataID,
      id,
      isSharedMeal,
      lazyGetOrCreateDbPlannerWeekByStartDate,
      lazyGetMeal,
      lazyGetSharedMeal,
      tracker,
      updateDbPlannerWeek,
      updateMeal,
      userProfile,
    ],
  );

  const [lazyGetProgrammes] = useLazyGetProgrammesQuery();

  const [lazyGetSmorgBoard] = useLazyGetSmorgBoardQuery();

  const onCopyToProgramme = useCallback(
    async (programmeId, planId, dayIndexes) => {
      const { data: programmes } = await lazyGetProgrammes(null, true);

      const programme = programmes.find((pr) => pr.id === programmeId);
      const plan = programme.plans.find((pl) => pl.id === planId);
      if (!plan) {
        return;
      }
      const dayIDs = dayIndexes.map((dayIndex) =>
        programmePlanDayIDFromDayIndex(dayIndex, plan.days),
      );
      const entryID = uuidv4();
      const updatedPlan = programmePlanWithEntryAdded(
        plan,
        dayIDs,
        entryID,
        EntryType.MEAL,
        id,
      );
      const updatedProgramme = programmeWithPlanUpdated(programme, updatedPlan);
      await updateProgramme(updatedProgramme);
      if (isSmorgStudio) {
        reportAccountActivitySignalOperation({
          spaceID: healthProSpace.id,
          friendlyUsername,
          lastActivityObjectType: ActivityObjectType.PROGRAMMES,
          lastActivityObjectID: programmeId,
        });
      }
    },
    [
      friendlyUsername,
      healthProSpace?.id,
      id,
      isSmorgStudio,
      lazyGetProgrammes,
      updateProgramme,
    ],
  );

  const onCopyToRecipesBoard = useCallback(
    async (toRecipesBoardId, toMenuId) => {
      const { data: destinationRecipesBoard } = await lazyGetSmorgBoard(
        toRecipesBoardId,
        true,
      );
      if (isSharedMeal) {
        const { data: fullSharedMeal } = await lazyGetSharedMeal(id);
        await copySharedMealToBoard(
          fullSharedMeal,
          destinationRecipesBoard,
          toMenuId,
          currentSpaceMembership,
          updateSmorgBoard,
          createMeal,
          userProfile,
          tracker,
          healthProSpace,
          null,
          dispatch,
        );
      } else {
        const { data: fullMeal } = await lazyGetMeal(id);
        await copyMealToBoard(
          fullMeal,
          destinationRecipesBoard,
          toMenuId,
          currentSpaceMembership,
          updateSmorgBoard,
          createMeal,
          userProfile,
          tracker,
          healthProSpace,
          null,
          dispatch,
          null,
        );
      }
      if (isSmorgStudio) {
        reportAccountActivitySignalOperation({
          spaceID: healthProSpace.id,
          friendlyUsername,
          lastActivityObjectType: ActivityObjectType.RECIPES,
          lastActivityObjectID: recipesBoardId,
        });
      }
    },
    [
      createMeal,
      currentSpaceMembership,
      dispatch,
      friendlyUsername,
      healthProSpace,
      id,
      isSharedMeal,
      isSmorgStudio,
      lazyGetMeal,
      lazyGetSharedMeal,
      lazyGetSmorgBoard,
      recipesBoardId,
      tracker,
      updateSmorgBoard,
      userProfile,
    ],
  );

  const onMoveToRecipesBoard = useCallback(
    async (toRecipesBoardId, toMenuId) => {
      if (recipesBoardId === toRecipesBoardId) {
        const newBoard = boardWithMealMoved(recipesBoard, id, menuID, toMenuId);
        await updateSmorgBoard(newBoard);
        if (isSmorgStudio) {
          reportAccountActivitySignalOperation({
            spaceID: healthProSpace.id,
            friendlyUsername,
            lastActivityObjectType: ActivityObjectType.RECIPES,
            lastActivityObjectID: recipesBoardId,
          });
        }
        return;
      }
      const { data: destinationRecipesBoard } = await lazyGetSmorgBoard(
        toRecipesBoardId,
        true,
      );
      const { data: fullMeal } = await lazyGetMeal(id);
      const updatedMeal = mealWithParentUpdated(fullMeal, toRecipesBoardId);
      await updateMeal(updatedMeal);
      const newSourceRecipesBoard = boardWithMealRemoved(
        recipesBoard,
        id,
        menuID,
      );
      const newDestinationRecipesBoard = boardWithMealAdded(
        destinationRecipesBoard,
        toMenuId,
        { id },
      );
      await Promise.all(
        [newSourceRecipesBoard, newDestinationRecipesBoard].map((board) =>
          updateSmorgBoard(board),
        ),
      );
    },
    [
      friendlyUsername,
      healthProSpace?.id,
      id,
      isSmorgStudio,
      lazyGetMeal,
      lazyGetSmorgBoard,
      menuID,
      recipesBoard,
      recipesBoardId,
      updateMeal,
      updateSmorgBoard,
    ],
  );

  const onAddToMealBasket = useCallback(
    async (scaleToServings) => {
      trackEvents([
        { name: 'Meals added to basket', args: { source: 'recipes' } },
      ]);
      const { data: fullMeal } = await lazyGetMeal(id);
      let scaledMeal;
      if (scaleToServings) {
        const scaleFactor =
          scaleToServings / (fullMeal.recipes[0]?.servings || 1);
        const scaledIngredients = await scaleMealIngredientsOperation(
          fullMeal,
          scaleFactor,
          customerFoodDataID,
        );
        scaledMeal = mealWithScaledIngredientsReplaced(
          fullMeal,
          scaleToServings,
          scaledIngredients,
          scaleFactor,
        );
      } else {
        scaledMeal = fullMeal;
      }
      dispatch(mealsAddedToMealBasket([scaledMeal]));
    },
    [customerFoodDataID, dispatch, id, lazyGetMeal],
  );
  const onRemoveFromMealBasket = useCallback(
    () => dispatch(mealsRemovedFromMealBasket([id])),
    [dispatch, id],
  );
  const setGlobalSnackbar = useCallback(
    (obj) => dispatch(showGlobalSnackbar(obj)),
    [dispatch],
  );
  const onClickCard = useCallback(() => onClick(id), [onClick, id]);
  const onClickDelete = useCallback(() => onDelete(id), [onDelete, id]);

  const { data: ownedRecipesBoards } = useGetOwnedRecipesBoardsQuery();

  const { data: ownedProgrammes } = useGetProgrammesQuery(undefined, {
    skip: !userIsCreator(userProfile),
  });

  let copyToBoardEnabled =
    (ownedRecipesBoards || []).length + (ownedProgrammes || []).length > 1;
  if (isSharedMeal) {
    copyToBoardEnabled =
      (ownedRecipesBoards || []).length + (ownedProgrammes || []).length > 0;
  }

  const boardAssociatedWithProgrammeIDs = programmesWhichReferenceRecipesBoard(
    recipesBoardId,
    ownedProgrammes || [],
  ).map((pr) => pr.id);
  const isBoardAssociatedWithProgrammes =
    boardAssociatedWithProgrammeIDs.length > 0;
  const destinationProgrammeIDs = [];
  if (boardEmbeddedInProgrammeID) {
    destinationProgrammeIDs.push(boardEmbeddedInProgrammeID);
  }
  destinationProgrammeIDs.push(...boardAssociatedWithProgrammeIDs);

  if (!title) {
    return null;
  }

  return (
    <MovableCardWrapper
      data-id={id}
      onClick={onClickCard}
      style={style}
      className={className}>
      <Card sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex' }}>
          <CardMedia
            component="img"
            className="recipesCardImage"
            image={mealImageUrl || imgPlaceholder}
            title={title}
          />
          <CardContent
            title={title}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              borderBottom: '1px solid #7B7B7B',
            }}>
            <Typography variant="body2" component="p" sx={{ flexGrow: 1 }}>
              {title}
            </Typography>
            <Grid container>
              <Grid item xs={6} className="mealCardNutrition">
                {calories && (
                  <Nutrition metric="calories" quantity={calories} />
                )}
              </Grid>
              <Grid item xs={6}>
                &nbsp;
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex' }}>
              {sharedProgrammesLinked && sharedProgrammesLinked.length > 0 && (
                <Tooltip
                  title="This recipe is linked to a live program"
                  placement="top-start">
                  <img
                    src={live_icon}
                    alt="This recipe is linked to a live program"
                    style={{
                      width: 36,
                      height: 36,
                    }}
                  />
                </Tooltip>
              )}
              {isSmorgStudio && allIngsStatus === 'warning' && (
                <WarningIcon
                  style={{
                    margin: 'auto 10px auto 0',
                    color: 'orange',
                  }}
                />
              )}
              {isSmorgStudio && allIngsStatus === 'info' && (
                <InfoIcon
                  style={{
                    margin: 'auto 10px auto 0',
                    color: 'orange',
                  }}
                />
              )}
              <MealTypesChips mealTypes={mealTypes} />
            </Box>
          </CardContent>
        </Box>
        <MealCardActions
          recipesBoardId={recipesBoardId}
          copyToBoardEnabled={copyToBoardEnabled}
          isReadOnly={isReadOnly}
          onQuickDuplicate={onQuickDuplicate}
          onCopyToPlanner={onCopyToPlanner}
          copyToProgrammeEnabled={
            isBoardEmbeddedInProgramme || isBoardAssociatedWithProgrammes
          }
          destinationProgrammeIDs={destinationProgrammeIDs}
          onCopyToProgramme={onCopyToProgramme}
          onCopyToRecipesBoard={onCopyToRecipesBoard}
          organizeEnabled={!userIsCreator}
          onMoveToRecipesBoard={onMoveToRecipesBoard}
          onDelete={onClickDelete}
          isInMealBasket={isInMealBasket}
          onAddToMealBasket={onAddToMealBasket}
          onRemoveFromMealBasket={onRemoveFromMealBasket}
          setGlobalSnackbar={setGlobalSnackbar}
          quickDuplicateEnabled={!isReadOnly && !isMobile}
          sharedProgrammesLinked={sharedProgrammesLinked}
        />
      </Card>
    </MovableCardWrapper>
  );
};

MealCard.propTypes = {
  onClick: PropTypes.func.isRequired,
  className: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  appBoardId: PropTypes.string.isRequired,
  meal: PropTypes.shape({
    id: PropTypes.string,
    recipes: PropTypes.arrayOf(
      PropTypes.shape({
        imageUrl: PropTypes.string,
        mealTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
      }),
    ),
  }).isRequired,
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default MealCard;
